import React, { useState } from 'react';
import styled from 'styled-components';
import { useSelector } from 'react-redux';
import SlideLoading from './SlideLoading';
import SmallDoughnut from 'common/components/SmallDoughnut';
import { useWelcomeTourData } from '../useWelcomeTourData';
import { getSelectedAssessmentPeriod } from 'common/selectors/auth';
import { HeroHeadline, HeroLabel, SlideContainer, HeroBanner, HeroBody, BannerDecoClip, HeroStrong, EmptyState } from './slideStyles';
import { useApiPortalV1GlossaryDataRetrieveQuery } from 'store/generated/portal/glossary';
import { scoreToColor, getSentimentSymbol } from '@/areas/dashboard/components/DashboardTeam.utils';
import { PILLARS } from '@/common/constants/pillars';

interface DimensionCardData {
    name: string;
    question: string;
}

const DIMENSIONS: DimensionCardData[] = PILLARS.map((pillar) => ({
    name: pillar.name,
    question: pillar.question,
}));


const DimensionGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.0rem;
  align-items: stretch;
  align-content: start;
  min-height: 0;
  padding: 1.5rem 1.5rem;

  @media (max-width: 1100px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 700px) {
    grid-template-columns: 1fr;
    padding-left: 0;
    padding-right: 0;
  }

    @media (max-width: 600px) and (orientation: portrait) {
        padding-top: 1rem;
        padding-bottom: 1rem;
    }
`;

const DimensionCard = styled.div`
  background: #ffffff;
  border: 1px solid #D1D5DB;
  border-radius: 0.65rem;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.18);
  padding: 1.25rem;
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 0.6rem;
  min-height: 180px;
`;

const DimensionCardBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  flex: 1;
  min-width: 0;
`;

const DimensionName = styled.div`
  font-family: 'Poppins', sans-serif;
  font-size: 15px;
  font-weight: 600;
  color: #1F2937;
  margin-left: 0.2rem;
`;

const DimensionQuestion = styled.div`
  font-family: 'Poppins', sans-serif;
  font-size: 12px;
  font-weight: 500;
  color: #334155;
  font-style: italic;
  line-height: 1.2;
  margin-left: 0.2rem;
`;

const DimensionThemeText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  font-family: 'Poppins', sans-serif;
  font-size: 13px;
  color: #6B7280;
  line-height: 1.4;
  margin-top: 0.6rem;
  margin-left: 0.35rem;
  white-space: normal;
  overflow: visible;
  text-overflow: unset;
`;

const ThemeItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.2rem;
`;

const ThemeStatusIcon = styled.span<{ $color: string }>`
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: ${props => props.$color};
  color: #ffffff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  line-height: 1;
  font-weight: 700;
  flex-shrink: 0;
  margin-left: 0.18rem;
`;

const DimensionTitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.3rem;
`;

const LegendRow = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1.0rem;
  flex-wrap: wrap;
  margin-top: 16px;
  padding: 0 1.5rem 0;

    @media (max-width: 600px) and (orientation: portrait) {
        padding: 0 0.5rem 0;
    }
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #6B7280;
  font-family: 'Poppins', sans-serif;
  font-size: 13px;
  text-align: center;
`;

const LegendSymbol = styled.span<{ $color?: string }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 999px;
  background: ${props => props.$color || '#E5E7EB'};
  color: ${props => (props.$color ? '#ffffff' : '#111827')};
  font-size: 14px;
  line-height: 1;
  font-weight: 700;
`;

const ExpandedSlideContainer = styled(SlideContainer)`
  height: 100%;
  flex: 1;
  min-height: 0;
`;

const ContentContainer = styled.div`
  width: min(100%, 1140px);
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;


const HeaderPanel = styled.div`
  width: 100%;
  padding: 0.75rem 1.75rem 0rem;
  box-sizing: border-box;
`;

const HeaderGrid = styled.div`
  display: grid;
  align-items: start;

  @media (max-width: 900px) {
    display: flex;
    flex-direction: column;
  }
`;

// --- Component ---

const Slide5CultureDimensions: React.FC = () => {
    const selectedAssessmentPeriod = useSelector(getSelectedAssessmentPeriod);
    const { pillars, isLoading: pillarsLoading } = useWelcomeTourData();
    const { data: glossaryRaw, isLoading: glossaryLoading } = useApiPortalV1GlossaryDataRetrieveQuery();
    const isLoading = pillarsLoading || glossaryLoading;

    const legendItems = [
        { symbol: '+', label: 'Positive sentiment' },
        { symbol: '•', label: 'Mixed sentiment' },
        { symbol: '-', label: 'Negative sentiment' },
    ];

    return (
        <ExpandedSlideContainer>
            <HeroBanner>
                <BannerDecoClip />
                <ContentContainer>
                    <HeaderPanel>
                        <HeaderGrid>

                            <HeroLabel>Cultural experience - {selectedAssessmentPeriod?.name || 'Current Assessment Period'}</HeroLabel>

                            <HeroHeadline>
                                How does your team experience culture?
                            </HeroHeadline>
                            <HeroBody>
                                <span>
                                    Based on your team’s scores and written feedback, these are the strengths, mixed experiences, and concerns that show where your leadership can have the greatest impact.
                                </span>
                            </HeroBody>
                        </HeaderGrid>
                    </HeaderPanel>

                    {isLoading ? (
                        <SlideLoading message="Loading insights..." />
                    ) : (<>
                        <DimensionGrid>
                            {DIMENSIONS
                                .map(d => {
                                    const pillar = pillars.find((p) => p.name.toLowerCase() === d.name.toLowerCase());
                                    return { ...d, pillar };
                                })
                                .filter(item => {
                                    const pillar = item.pillar;
                                    const hasScore = typeof pillar?.score === 'number' && !Number.isNaN(pillar.score);
                                    const hasThemes = Array.isArray(pillar?.themes) && pillar.themes.length > 0;
                                    const hasSummary = typeof pillar?.summary === 'string' && pillar.summary.trim() !== '';
                                    return pillar && (hasScore || hasThemes || hasSummary);
                                })
                                .sort((a, b) => {
                                    const aOrder = a.pillar?.sort_order ?? Number.MAX_SAFE_INTEGER;
                                    const bOrder = b.pillar?.sort_order ?? Number.MAX_SAFE_INTEGER;
                                    if (aOrder !== bOrder) return aOrder - bOrder;
                                    return a.name.localeCompare(b.name);
                                })
                                .map(d => {
                                    const pillar = d.pillar;
                                    const themes = Array.isArray(pillar?.themes) ? pillar.themes : [];
                                    const summaryText = typeof pillar?.summary === 'string' ? pillar.summary : null;
                                    const scoredThemes = themes
                                        .map((t) => ({
                                            label: `${t.label}`,
                                            score: typeof t.sentiment === 'number' ? t.sentiment : undefined,
                                        }))
                                        .filter((item): item is { label: string; score: number } => typeof item.score === 'number');

                                    const themeItems = (() => {
                                        if (scoredThemes.length > 0) {
                                            const sortedByScore = scoredThemes.slice().sort((a, b) => b.score - a.score);
                                            if (sortedByScore.length >= 3) {
                                                const middleIndex = Math.floor((sortedByScore.length - 1) / 2);
                                                return [sortedByScore[0], sortedByScore[middleIndex], sortedByScore[sortedByScore.length - 1]];
                                            }
                                            return sortedByScore;
                                        }

                                        if (summaryText) {
                                            return [{ label: summaryText, score: undefined }];
                                        }

                                        if (pillar && typeof pillar.score === 'number') {
                                            return [{ label: 'Not enough feedback to provide themes', score: undefined }];
                                        }

                                        return [{ label: d.question, score: undefined }];
                                    })();
                                    const themeTitle = themeItems.map((item) => item.label).join('\n');

                                    return (
                                        <DimensionCard key={d.name}>
                                            <SmallDoughnut
                                                value={pillar?.score ?? 0}
                                                colorized
                                                size={48}
                                            />
                                            <DimensionCardBody>
                                                <DimensionTitleRow>
                                                    <DimensionName>{d.name}</DimensionName>
                                                </DimensionTitleRow>
                                                <DimensionQuestion>{d.question}</DimensionQuestion>
                                                <DimensionThemeText title={themeTitle || undefined}>
                                                    {themeItems.map((item, index) => (
                                                        <ThemeItem key={index}>
                                                            <ThemeStatusIcon $color={scoreToColor(item.score ? item.score * 10 : 0)}>
                                                                {getSentimentSymbol(item.score ? item.score * 10 : 0)}
                                                            </ThemeStatusIcon>
                                                            <span>
                                                                {item.label}
                                                            </span>
                                                        </ThemeItem>
                                                    ))}
                                                </DimensionThemeText>
                                            </DimensionCardBody>
                                        </DimensionCard>
                                    );
                                })}
                        </DimensionGrid>
                        <LegendRow>
                            {legendItems.map((item) => (
                                <LegendItem key={item.symbol}>
                                    <LegendSymbol $color={scoreToColor(item.symbol === '+' ? 10 : item.symbol === '-' ? 0 : 6)}>{item.symbol}</LegendSymbol>
                                    <span>{item.label}</span>
                                </LegendItem>
                            ))}
                        </LegendRow>
                    </>

                    )}

                </ContentContainer>
            </HeroBanner>

        </ExpandedSlideContainer>
    );
};

export default Slide5CultureDimensions;
